import { Reference } from '@approvalmax/types';
import { useAtomValue } from 'jotai';
import debounce from 'lodash/debounce';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useMemo, useState } from 'react';
import { useGetNetSuiteCustomer, useGetNetSuiteShippingMethods } from 'shared/data/webApp/v2';

import { getRequest } from '../../../selectors/pageSelectors';
import { convertIntegrationCodeToDocumentType } from '../helpers';
import { mapCustomerData } from '../NetSuiteInvoiceRequestForm/NetSuiteInvoiceRequestForm.helpers';
import { netSuiteInvoiceFieldValuesState } from '../NetSuiteInvoiceRequestForm/NetSuiteInvoiceRequestForm.states';

export const useShippingAddresses = () => {
    const request = useSelector<domain.NetSuiteInvoiceRequest>(getRequest);
    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);

    const { data, isFetching } = useGetNetSuiteCustomer({
        path: {
            companyId: request.companyId,
            customerId: fieldValues.customer?.id,
            documentType: convertIntegrationCodeToDocumentType(domain.IntegrationCode.NetSuiteInvoice),
        },
    });

    const customerData = useMemo(() => mapCustomerData(data), [data]);

    return {
        shippingAddressesItems: customerData?.addresses || [],
        isFetchingShippingAddresses: isFetching,
    };
};

export const useShippingMethods = () => {
    const request = useSelector<domain.NetSuiteInvoiceRequest>(getRequest);
    const fieldValues = useAtomValue(netSuiteInvoiceFieldValuesState);

    const [query, setQuery] = useState('');

    const handleSearchShippingMethod = debounce((text: string) => setQuery(text), 500);

    const { data, isFetching } = useGetNetSuiteShippingMethods({
        path: {
            companyId: request.companyId,
            documentType: convertIntegrationCodeToDocumentType(domain.IntegrationCode.NetSuiteInvoice),
        },
        query: {
            customerId: fieldValues.customer?.id,
            query: query,
        },
    });

    const shippingMethodsItems = useMemo(() => {
        return (data ?? []).reduce<Reference[]>((shippingMethods, { id, name }) => {
            if (id && name) {
                shippingMethods.push({ id, text: name });
            }

            return shippingMethods;
        }, []);
    }, [data]);

    return {
        shippingMethodsItems,
        isFetchingShippingMethods: isFetching,
        handleSearchShippingMethod,
    };
};
